import { type ReactNode, useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'framer-motion';
import { clsx } from 'clsx';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  position?: 'bottom' | 'right';
  className?: string;
  title?: string;
  enableSnapPoints?: boolean;
}

const VELOCITY_THRESHOLD = 500; // velocity to trigger close/snap

// Snap points as percentage of viewport height (0 = closed, 1 = full)
const SNAP_POINTS = {
  closed: 0,
  half: 0.4,
  full: 0.9,
};

export function Drawer({
  isOpen,
  onClose,
  children,
  position = 'bottom',
  className,
  title,
  enableSnapPoints = true,
}: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const [snapPoint, setSnapPoint] = useState<'half' | 'full'>('half');
  
  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !drawerRef.current) return;

    const drawer = drawerRef.current;
    const focusableElements = drawer.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstElement?.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    drawer.addEventListener('keydown', handleTab);
    return () => drawer.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Calculate drawer height based on snap point
  const getDrawerHeight = useCallback((point: 'half' | 'full') => {
    return windowHeight * SNAP_POINTS[point];
  }, [windowHeight]);

  // Handle drag end for bottom drawer with snap points
  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (!enableSnapPoints || position !== 'bottom') {
        // Simple close logic for non-snap mode
        if (info.offset.y > 100 || info.velocity.y > VELOCITY_THRESHOLD) {
          onClose();
        }
        return;
      }

      const velocity = info.velocity.y;
      const offset = info.offset.y;
      const currentHeight = getDrawerHeight(snapPoint);
      
      // Fast swipe down = close
      if (velocity > VELOCITY_THRESHOLD) {
        if (snapPoint === 'half' || offset > currentHeight * 0.5) {
          onClose();
        } else {
          setSnapPoint('half');
        }
        return;
      }
      
      // Fast swipe up = full
      if (velocity < -VELOCITY_THRESHOLD) {
        setSnapPoint('full');
        return;
      }
      
      // Slow drag - snap based on position
      const draggedHeight = currentHeight - offset;
      const halfHeight = getDrawerHeight('half');
      const fullHeight = getDrawerHeight('full');
      
      if (draggedHeight < halfHeight * 0.5) {
        // Below half of half-point - close
        onClose();
      } else if (draggedHeight < (halfHeight + fullHeight) / 2) {
        // Between half and midpoint - snap to half
        setSnapPoint('half');
      } else {
        // Above midpoint - snap to full
        setSnapPoint('full');
      }
    },
    [onClose, enableSnapPoints, position, snapPoint, getDrawerHeight]
  );

  // Reset snap point when drawer opens
  useEffect(() => {
    if (isOpen) {
      setSnapPoint('half');
    }
  }, [isOpen]);

  const variants = {
    bottom: {
      initial: { y: '100%' },
      animate: { 
        y: 0,
        height: enableSnapPoints ? getDrawerHeight(snapPoint) : undefined,
      },
      exit: { y: '100%' },
    },
    right: {
      initial: { x: '100%' },
      animate: { x: 0 },
      exit: { x: '100%' },
    },
  };

  const positionStyles = {
    bottom: enableSnapPoints 
      ? 'inset-x-0 bottom-0 rounded-t-2xl' 
      : 'inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl',
    right: 'right-0 top-0 bottom-0 w-full max-w-md',
  };

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  const springTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring', damping: 25, stiffness: 300 };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black z-drawer"
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={title || 'Drawer'}
            initial={variants[position].initial}
            animate={variants[position].animate}
            exit={variants[position].exit}
            transition={springTransition}
            drag={position === 'bottom' ? 'y' : false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.1, bottom: 0.3 }}
            onDragEnd={position === 'bottom' ? handleDragEnd : undefined}
            className={clsx(
              'fixed bg-white shadow-xl z-drawer overflow-hidden flex flex-col',
              positionStyles[position],
              className
            )}
            style={{ 
              willChange: 'transform',
              // Overscroll bounce effect
              overscrollBehavior: 'contain',
            }}
          >
            {/* Drag handle for bottom drawer - 32px wide, 4px tall */}
            {position === 'bottom' && (
              <div
                className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none select-none"
                onPointerDown={(e) => dragControls.start(e)}
                role="slider"
                aria-label="Drag to resize drawer"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={snapPoint === 'full' ? 100 : 50}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    setSnapPoint('full');
                  } else if (e.key === 'ArrowDown') {
                    if (snapPoint === 'full') {
                      setSnapPoint('half');
                    } else {
                      onClose();
                    }
                  }
                }}
              >
                <div className="w-8 h-1 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
              </div>
            )}

            {/* Close button for right drawer */}
            {position === 'right' && (
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close drawer"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
